import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, FileText, UserCheck, TrendingUp, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_leads: 0,
    total_subscribers: 0,
    total_messages: 0,
    total_posts: 0,
    unread_messages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { 
      title: 'Leads', 
      value: stats.total_leads, 
      icon: UserCheck, 
      color: 'bg-blue-500',
      description: 'Total des leads capturés'
    },
    { 
      title: 'Abonnés Newsletter', 
      value: stats.total_subscribers, 
      icon: Users, 
      color: 'bg-green-500',
      description: 'Inscrits à la newsletter'
    },
    { 
      title: 'Messages', 
      value: stats.total_messages, 
      icon: Mail, 
      color: 'bg-purple-500',
      description: `${stats.unread_messages} non lus`
    },
    { 
      title: 'Articles', 
      value: stats.total_posts, 
      icon: FileText, 
      color: 'bg-orange-500',
      description: 'Articles de blog'
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-rm-text">Dashboard</h1>
        <p className="text-rm-text-secondary">Vue d'ensemble de ton activité</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-rm-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-rm-text">
                        {loading ? '-' : stat.value}
                      </p>
                      <p className="text-xs text-rm-text-secondary mt-1">{stat.description}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-rm-primary" />
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a 
                href="/admin/blog/new"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">Créer un nouvel article</p>
                <p className="text-sm text-rm-text-secondary">Publier du contenu sur le blog</p>
              </a>
              <a 
                href="/admin/contacts"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">
                  Voir les messages {stats.unread_messages > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-rm-secondary text-white text-xs rounded-full">
                      {stats.unread_messages} nouveau(x)
                    </span>
                  )}
                </p>
                <p className="text-sm text-rm-text-secondary">Répondre aux demandes de contact</p>
              </a>
              <a 
                href="/admin/leads"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">Gérer les leads</p>
                <p className="text-sm text-rm-text-secondary">Voir et exporter les leads capturés</p>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-rm-primary" />
              Aperçu du site
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a 
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">Page d'accueil</p>
                <p className="text-sm text-rm-text-secondary">Voir la landing page</p>
              </a>
              <a 
                href="/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">Blog</p>
                <p className="text-sm text-rm-text-secondary">Voir les articles publiés</p>
              </a>
              <a 
                href="/tarifs"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <p className="font-medium text-rm-text">Tarifs</p>
                <p className="text-sm text-rm-text-secondary">Voir la page des tarifs</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
